import type { PostHog } from "posthog-js";
import type { MerchantAnalyticsContext } from "@jurni/schemas/browser/merchant-analytics";
import type { ShopSummary } from "@jurni/schemas";
import type { MerchantAnalyticsEvents } from "./events";
import { getAnalyticsPage } from "./page";
import { sanitizeAnalyticsPathname, sanitizeAnalyticsUrl } from "./privacy";
import {
  createMerchantSessionReplay,
  type SessionReplaySdk,
} from "./session-replay";

type AnalyticsSdk = Pick<
  PostHog,
  | "capture"
  | "identify"
  | "reset"
  | "get_property"
  | "register"
  | "unregister"
  | "group"
  | "getGroups"
  | "resetGroups"
> &
  SessionReplaySdk;

export function createMerchantAnalytics(
  sdk: AnalyticsSdk,
  options: {
    enabled: boolean;
    environment: string;
    getLocation: () => { pathname: string; href: string };
  },
) {
  const sessionReplay = createMerchantSessionReplay(sdk);
  let context: MerchantAnalyticsContext | undefined;
  let lastPageView: string | undefined;
  let identifiedShop: Pick<ShopSummary, "id" | "name" | "domain"> | undefined;

  // Analytics must never turn a successful merchant operation into a UI error.
  function safely(action: () => void) {
    if (!options.enabled) return;
    try {
      action();
    } catch {
      console.warn("Merchant analytics could not record an event");
    }
  }

  function syncIdentity(
    next: MerchantAnalyticsContext,
    previous: MerchantAnalyticsContext | undefined,
  ) {
    const identifiedUser = sdk.get_property("$user_id");
    // Another tab can update the SDK identity before this tab receives its session.
    // A change in the trusted user must still end this tab's recording identity.
    if (
      (identifiedUser && identifiedUser !== next.userId) ||
      (previous?.userId && previous.userId !== next.userId)
    ) {
      sessionReplay.stop();
      sdk.reset();
    }
    if (
      next.userId &&
      (sdk.get_property("$user_id") !== next.userId ||
        sdk.get_property("is_admin") !== next.isAdmin ||
        previous?.userId !== next.userId ||
        previous.email !== next.email ||
        previous.name !== next.name)
    ) {
      sdk.identify(next.userId, {
        is_admin: next.isAdmin,
        ...(next.email !== null ? { email: next.email } : {}),
        ...(next.name !== null ? { name: next.name } : {}),
      });
    }
    // Keep the role on SDK-generated events without persisting identity fields
    // as common event properties.
    if (next.userId) {
      sdk.register({ is_admin: next.isAdmin });
    } else {
      sdk.unregister("is_admin");
    }
  }

  function syncGroups(next: MerchantAnalyticsContext) {
    const shopId = next.shopId ? String(next.shopId) : undefined;
    const organizationId = next.organizationId || undefined;
    const groups = sdk.getGroups();
    if (groups.shop !== shopId || groups.organization !== organizationId) {
      sdk.resetGroups();
      if (shopId) sdk.group("shop", shopId);
      if (organizationId) sdk.group("organization", organizationId);
    }
  }

  function setContext(next: MerchantAnalyticsContext) {
    const previous = context;
    context = undefined;
    if (!next.userId || previous?.shopId !== next.shopId) {
      identifiedShop = undefined;
    }
    sessionReplay.setUser(next.userId);
    safely(() => {
      syncIdentity(next, previous);
      syncGroups(next);
      context = next;
    });
  }

  function identifyShop(shop: Pick<ShopSummary, "id" | "name" | "domain">) {
    safely(() => {
      const name = shop.name.trim();
      const domain = shop.domain ?? null;
      // The server shop summary must match the authenticated scope, not persisted UI state.
      if (
        !context?.userId ||
        context.shopId !== shop.id ||
        sdk.getGroups().shop !== String(shop.id) ||
        !name ||
        (identifiedShop?.id === shop.id &&
          identifiedShop.name === name &&
          identifiedShop.domain === domain)
      )
        return;
      sdk.group("shop", String(shop.id), { name, shopDomain: domain });
      identifiedShop = { id: shop.id, name, domain };
    });
  }

  function setOnboardingContext(onboardingId: string | null) {
    safely(() => {
      if (onboardingId) {
        sdk.register({
          is_onboarding_session: true,
          onboarding_id: onboardingId,
        });
      } else {
        sdk.unregister("is_onboarding_session");
        sdk.unregister("onboarding_id");
      }
    });
  }

  function capture(event: string, properties: object) {
    safely(() => {
      if (!context) return;
      const location = options.getLocation();
      const page = getAnalyticsPage(location.pathname);
      if (!page) return;
      const currentUrl = sanitizeAnalyticsUrl(location.href);
      sdk.capture(event, {
        ...properties,
        app: "merchant",
        analytics_version: 1,
        environment: options.environment,
        ...page,
        $current_url: currentUrl,
        $pathname: sanitizeAnalyticsPathname(location.pathname),
        pageUrl: currentUrl,
        shop_id: context.shopId ? String(context.shopId) : null,
        shopId: context.shopId,
        shopName: identifiedShop?.domain ?? null,
        shopDomain: identifiedShop?.domain ?? null,
        ...(event === "app_page_view" ? { email: context.email } : {}),
        organization_id: context.organizationId,
        is_admin: context.isAdmin,
        is_authenticated: Boolean(context.userId),
      });
    });
  }

  function track<Event extends keyof MerchantAnalyticsEvents>(
    event: Event,
    properties: MerchantAnalyticsEvents[Event],
  ) {
    capture(event, properties);
  }

  function pageView() {
    safely(() => {
      if (!context) return;
      const key = JSON.stringify([
        context.userId,
        context.shopId,
        context.organizationId,
        options.getLocation().pathname,
      ]);
      if (key === lastPageView) return;
      lastPageView = key;
      capture("$pageview", {});
      // Retain the existing event for saved PostHog reports. Use $pageview for new reports.
      if (context.shopId) capture("app_page_view", {});
    });
  }

  function reset() {
    context = undefined;
    lastPageView = undefined;
    identifiedShop = undefined;
    sessionReplay.setUser(null);
    safely(() => {
      sessionReplay.stop();
      sdk.reset();
    });
  }

  function setSessionRecordingEnabled(enabled: boolean) {
    safely(() => {
      if (
        enabled &&
        context?.userId &&
        getAnalyticsPage(options.getLocation().pathname)
      ) {
        sessionReplay.start();
      } else {
        sessionReplay.stop();
      }
    });
  }

  return {
    track,
    setContext,
    identifyShop,
    setOnboardingContext,
    pageView,
    reset,
    setSessionRecordingEnabled,
    canCaptureReplaySnapshot: sessionReplay.canCaptureSnapshot,
  };
}
